/**
 * 로그인한 관리자를 앱 전체가 아는 자리.
 *
 * ⚠ **거절의 갈래를 여기서 다룬다**(명세 2-5). 요청 하나가 실패했을 때 로그아웃할지,
 * 키패드를 띄울지, 처음 설정을 띄울지, 「권한 없음」이라 할지, 「계정이 꺼져 있다」고
 * 할지를 **한 곳에서** 정한다.
 *
 * 메뉴·버튼의 근거는 XM-06 의 `superAdmin`·`keys` 하나다(명세 0-5). 역할 이름·권한
 * 이름으로 짐작하지 않는다.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchMe } from '@/api/admin'
import { ApiError } from '@/api/errors'
import { clearToken, getToken, setToken } from '@/auth/token'
import { onAuthFailure } from '@/auth/authEvents'
import { holds } from '@/auth/nav'
import { forgetWrite } from '@/auth/pendingWrite'
import type { AdminMe, ButtonKey } from '@/api/types'

interface SessionValue {
  me: AdminMe | null
  /** 로그인이 끝났을 때 부른다 — 토큰을 저장하고 XM-06 을 읽는다. */
  signIn: (token: string) => void
  loading: boolean
  /** 첫 설정(XM-03)이 남은 세션이다 — 임시 비밀번호로 들어왔다. */
  setupNeeded: boolean
  /** 계정이 ACTIVE 가 아니다 — 권한 문제와 구분해서 다룬다. */
  inactive: boolean
  /** 이 세션이 키패드를 아직 안 지났다. */
  pinNeeded: boolean
  /** 최고관리자 — 모든 키를 가지고, 직원 관리·권한 이름은 이것으로만 연다. */
  superAdmin: boolean
  /** 넘긴 키를 **모두** 가졌는가(레거시 `has` 그대로 — 버튼 조건). */
  has: (...need: ButtonKey[]) => boolean
  refresh: () => Promise<void>
  /**
   * 토큰을 버리고 로그인 화면으로. `notice` 를 주면 로그인 화면에 그 까닭을 남긴다 — 정할 것 21·34·37 최종 잠금(서버가 그 계정의
   * 세션을 모두 끝냈다). 다음 로그인(signIn)에 지운다.
   */
  signOut: (notice?: string) => void
  /** 로그인 화면에 보일 「로그아웃된 까닭」. 없으면 null. */
  signOutNotice: string | null
}

const Ctx = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()

  // ⚠ **토큰을 상태로 든다.** 모듈 변수만 두면 sessionStorage 에 넣어도 React 가
  // 모른다 — 값이 바뀐 것을 알릴 길이 없어 화면이 로그인 단계에 그대로 멈춘다.
  const [token, setTokenState] = useState<string | null>(() => getToken())
  const [signOutNotice, setSignOutNotice] = useState<string | null>(null)

  const q = useQuery({
    queryKey: ['admin', 'me', token],
    queryFn: fetchMe,
    enabled: token !== null,
    retry: false,
    // ⚠ 권한 이름의 키는 최고관리자가 언제든 고친다(바로 적용 — 명세 2-4). 화면이
    // 오래된 키로 도는 것을 막으려고 창을 다시 볼 때마다 확인한다.
    refetchOnWindowFocus: true,
  })

  // ⚠ **세션이 바뀌는 두 자리에서 PIN 에 막힌 쓰기를 버린다**(pendingWrite.ts). 그
  // 쓰기는 그것을 누른 사람의 세션 것이다. 남겨 두면 401 로 EA-01 에 간 뒤 같은 탭에서
  // 로그인한 사람이 PIN 을 지나는 순간 확인 창 없이 그 사람 이름으로 나간다(돈이
  // 움직이는 승인이다). signOut 은 401·「로그아웃」·「다른 계정으로 로그인」이 모두 지난다.
  const signIn = useCallback((next: string) => {
    forgetWrite()
    setToken(next)
    setTokenState(next)
    setSignOutNotice(null)
  }, [])

  // ⚠ **글 없는 signOut 은 남은 까닭을 지우지 않는다.** 최종 잠금으로 나간 뒤 늦게 도착한 다른 요청의 401 이 한 번 더
  // signOut 을 부르는데(그 세션도 서버가 끝냈다), 그때 까닭이 지워지면 사람이 왜 나갔는지 모른다.
  const signOut = useCallback((notice?: string) => {
    clearToken()
    forgetWrite()
    setTokenState(null)
    if (notice !== undefined) setSignOutNotice(notice)
    qc.clear()
  }, [qc])

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['admin', 'me'] })
  }, [qc])

  // ⚠ **어느 요청에서 왔든 여기서 한 번만 다룬다** — 화면마다 흩어 놓으면
  // 언젠가 하나를 빠뜨리고, 빠뜨린 그 화면에서 PIN 만 안 푼 사람이 로그아웃당한다.
  useEffect(() => onAuthFailure((kind) => {
    if (kind === 'session_expired') {
      // 죽은 토큰을 들고 계속 두드리지 않는다.
      signOut()
      return
    }
    // ⚠ **로그아웃하지 않는다.** XM-06 을 다시 물으면 —
    //   pin_required      → pinVerified false → 키패드(EA-04)
    //   setup_required    → setupRequired true → 처음 설정(EA-03)
    //   forbidden(권한)   → 줄어든 keys 로 메뉴·버튼을 바로 다시 그림, 또는
    //                       status 가 ACTIVE 가 아니면 비활성 띠(명세 2-5, 제안 03 4번 ③)
    if (kind === 'pin_required' || kind === 'setup_required' || kind === 'permission_denied') {
      void qc.invalidateQueries({ queryKey: ['admin', 'me'] })
    }
  }), [qc, signOut])

  const me = q.data ?? null

  const value = useMemo<SessionValue>(() => {
    const access = { superAdmin: me?.superAdmin ?? false, keys: me?.keys ?? [] }
    return {
      me,
      signIn,
      loading: q.isLoading,
      setupNeeded: me !== null && me.setupRequired,
      // ⚠ 세션이 죽은 것과 계정이 꺼진 것은 다르다. 전자는 401 로 오고,
      // 후자는 XM-06 이 200 에 status 를 실어 온다.
      inactive: me !== null && me.status !== 'ACTIVE',
      pinNeeded: me !== null && !me.pinVerified,
      superAdmin: access.superAdmin,
      has: (...need) => me !== null && need.every((k) => holds(access, k)),
      refresh,
      signOut,
      signOutNotice,
    }
  }, [me, q.isLoading, refresh, signIn, signOut, signOutNotice])

  // 세션이 죽었으면 토큰을 버린다 — 죽은 토큰을 들고 계속 두드리지 않는다.
  if (q.error instanceof ApiError && q.error.kind === 'session_expired' && getToken()) {
    clearToken()
    forgetWrite()
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useSession(): SessionValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useSession was called outside SessionProvider')
  return v
}
